import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { map, tap } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';

import { UserType } from '@gymautoc/common/core/enums/user-type';
import { NavigationLink } from '@gymautoc/common/core/models/navigation-link';
import { filterNull } from '@gymautoc/common/core/rxjs/filter-null';
import { CurrentUserService } from '@gymautoc/common/core/services/current-user.service';
import { mainPageByUserType, routePaths } from '@gymautoc/common/core/utils/route-paths';

import { SidebarComponent } from './components/sidebar/sidebar.component';

const LINKS_MAP = new Map<UserType, NavigationLink[]>([
  [
    UserType.Admin, [
      {
        text: 'Users',
        route: routePaths.users,
      },
      {
        text: 'Scheduler',
        route: routePaths.scheduler,
      },
      {
        text: 'Holidays',
        route: routePaths.holidays,
      },
    ],
  ],
  [
    UserType.StudioManager, [
      {
        text: 'Instructors',
        route: routePaths.instructors,
      },
      {
        text: 'Students',
        route: routePaths.students,
      },
      {
        text: 'Classes',
        route: routePaths.classes,
      },
      {
        text: 'Studios',
        route: routePaths.studios,
      },
      {
        text: 'Scheduler',
        route: routePaths.scheduler,
      },
      {
        text: 'Holidays',
        route: routePaths.holidays,
      },
      {
        text: 'Payment History',
        route: routePaths.paymentHistory,
      },
      {
        text: 'Programs',
        route: routePaths.classGroup,
      },
      {
        text: 'Registrations',
        route: routePaths.enrollmentApplications,
      },
    ],
  ],
  [
    UserType.Staff, [
      {
        text: 'Students',
        route: routePaths.students,
      },
      {
        text: 'Classes',
        route: routePaths.classes,
      },
      {
        text: 'Scheduler',
        route: routePaths.scheduler,
      },
      {
        text: 'Holidays',
        route: routePaths.holidays,
      },
    ],
  ],
  [
    UserType.Parent, [
      {
        text: 'Students',
        route: routePaths.students,
      },
      {
        text: 'Registrations',
        route: routePaths.enrollmentApplications,
      },
    ],
  ],
]);

/**
 * Admin layout component.
 */
@Component({
  selector: 'gymautow-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SidebarComponent, AsyncPipe],
})
export class LayoutComponent {
  private readonly currentUserService = inject(CurrentUserService);

  private readonly router = inject(Router);

  /** Sidebar navigation links. */
  public readonly links$ = this.currentUserService.currentUser$.pipe(
    filterNull(),
    map(user => user.type),
    tap(type => {
      const { url } = this.router;

      // We don't have a home page. That's why we have to redirect user to his main page.
      if (url === routePaths.home[0]) {
        this.router.navigate(mainPageByUserType[type]);
      }
    }),
    map(type => LINKS_MAP.get(type) ?? []),
  );

  /** Studio name. */
  public readonly studioName$ = this.currentUserService.studioName$;

}
